import { useUserStore } from '@/stores/auth.ts';
import { config, identify, tag } from '@shared/helpers/tag';
import { useCoachStore } from '@shared/stores/coach';
import { createRouter, createWebHistory } from 'vue-router';

const isPayAsYouGoPlanEnabled = import.meta.env.VITE_ENABLE_PAY_AS_YOU_GO_PLAN === 'true';
console.log('[router] isPayAsYouGoPlanEnabled', isPayAsYouGoPlanEnabled);

const anonymousPublicRouteNames = new Set([
  'signin',
  'signup',
  'demo',
  'chat-demo',
  'strava-callback',
  'whoop-callback',
  'garmin-callback',
  'garmin-oauth2-callback',
  'polar-callback',
  'suunto-callback',
]);

// Loading state management
const loadingState = {
  isInitialLoad: true,
};

function hideLoadingIndicator() {
  const loadingIndicator = document.getElementById('loading-indicator');
  const appElement = document.getElementById('app');

  if (loadingIndicator && appElement) {
    appElement.classList.add('loaded');
    loadingIndicator.classList.add('hidden');

    setTimeout(() => {
      loadingIndicator.remove();
    }, 500);
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      component: () => import('../layouts/EmptyLayout.vue'),
      children: [
        {
          path: 'signin',
          name: 'signin',
          component: () => import('../views/SignInView.vue'),
          meta: {
            isSignInPage: true,
          },
        },
        {
          path: 'signup',
          name: 'signup',
          component: () => import('../views/SignInView.vue'),
          meta: {
            isSignInPage: true,
            signUp: true,
          },
        },
        {
          path: 'r/:refCode',
          name: 'referral',
          component: () => import('../views/RefferalLandingPageView.vue'),
          meta: {
            isSignInPage: false,
            authRequired: false,
          },
        },
        {
          path: 's/:refCode',
          name: 'referral-strava',
          component: () => import('../views/RefferalLandingPageView.vue'),
          meta: {
            isSignInPage: false,
            authRequired: false,
            channel: 'strava',
          },
        },
        {
          path: 'signout',
          name: 'signout',
          component: () => import('../views/SignOutView.vue'),
          meta: {
            isSignInPage: true,
          },
        },
        {
          path: 'campaign/race',
          name: 'race-entry-promotion',
          component: () => import('../views/RaceEntryPromotionLandingPageView.vue'),
          meta: {
            authRequired: false,
          },
        },
        {
          path: 'campaign/ask-a-coach',
          name: 'ask-a-coach-campaign',
          component: () => import('../views/AskHumanCoachCampaignLandingPageView.vue'),
          meta: {
            authRequired: false,
          },
        },
        {
          path: 'campaign/tokyo-marathon',
          name: 'tokyo-marathon-campaign',
          component: () => import('../views/TokyoMarathonCampaignLandingPageView.vue'),
          meta: {
            authRequired: false,
          },
        },
        {
          path: 'campaign/beyond-26',
          name: 'beyond-26-campaign',
          component: () => import('../views/Beyond26CampaignLandingPageView.vue'),
          meta: {
            authRequired: false,
          },
        },
        {
          path: 'demo',
          name: 'demo',
          component: () => import('../views/DemoView.vue'),
          meta: {
            authRequired: false,
          },
        },
        {
          path: 'auth/strava/callback',
          name: 'strava-callback',
          component: () => import('../views/signin/StravaCallbackView.vue'),
          meta: {
            isSignInPage: true,
          },
        },
        {
          path: 'auth/whoop/callback',
          name: 'whoop-callback',
          component: () => import('../views/signin/WhoopCallbackView.vue'),
          meta: {
            isSignInPage: true,
          },
        },
        {
          path: 'auth/garmin/oauth2/callback',
          name: 'garmin-oauth2-callback',
          component: () => import('../views/signin/GarminOauth2CallbackView.vue'),
          meta: {
            isSignInPage: true,
          },
        },
        {
          path: 'auth/polar/callback',
          name: 'polar-callback',
          component: () => import('../views/signin/PolarCallbackView.vue'),
          meta: {
            isSignInPage: true,
          },
        },
        {
          path: 'auth/suunto/callback',
          name: 'suunto-callback',
          component: () => import('../views/signin/SuuntoCallbackView.vue'),
          meta: {
            isSignInPage: true,
          },
        },
        {
          path: 'onboarding',
          name: 'onboarding',
          component: () => import('../views/OnboardingView.vue'),
          meta: {
            authRequired: true,
          },
        },
        {
          path: 'feedback',
          name: 'feedback',
          component: () => import('../views/FeedbackView.vue'),
          meta: {
            authRequired: true,
          },
        },
        {
          path: 'coaches',
          name: 'coaches',
          components: {
            default: () =>
              isPayAsYouGoPlanEnabled
                ? import('../views/PlanListPayPerUseView.vue')
                : import('../views/PlanListView.vue'),
            title: () => import('../views/DefaultTitleView.vue'),
          },
          meta: {
            authRequired: true,
          },
        },
        {
          path: 'welcome',
          name: 'welcome',
          components: {
            default: () =>
              isPayAsYouGoPlanEnabled
                ? import('../views/WelcomeViewPayPerUse.vue')
                : import('../views/WelcomeView.vue'),
            title: () => import('../views/DefaultTitleView.vue'),
          },
          meta: {
            authRequired: true,
          },
        },
        {
          path: 'coaches/:coachId/plans/:planId/stripe/callback',
          name: 'coaches-subscribe-stripe-callback',
          component: () => import('../views/CoachPlanSetupCallback.vue'),
          meta: {
            authRequired: true,
            upgrade: false,
          },
        },
        {
          path: 'coaches/:coachId/plans/:planId/stripe/upgrade',
          name: 'coaches-subscribe-stripe-upgrade',
          component: () => import('../views/CoachPlanSetupCallback.vue'),
          meta: {
            authRequired: true,
            upgrade: true,
          },
        },
        {
          path: 'coaches/:coachId/plans/:planId/stripe/upgrade-pay-as-you-go',
          name: 'coaches-subscribe-stripe-upgrade-pay-as-you-go',
          component: () => import('../views/CoachPlanSetupCallback.vue'),
          meta: {
            authRequired: true,
            upgrade: true,
            payAsYouGo: true,
          },
        },
        {
          path: 'coaches/:coachId/subscription/edit/stripe/callback',
          name: 'coaches-subscribe-edit-stripe-callback',
          component: () => import('../views/CoachDefaultPaymentUpdateStripeCallback.vue'),
          meta: {
            authRequired: true,
          },
        },
        {
          path: 'onboarding/update-profile',
          name: 'onboarding-update-profile',
          component: () => import('../views/OnboardingUpdateProfileView.vue'),
          meta: {
            authRequired: true,
          },
        },
        {
          path: 'subscriptions/:subscriptionId/payment',
          name: 'subscription-payment',
          component: () => import('../views/SubscriptionPaymentView.vue'),
          meta: {
            authRequired: true,
          },
        },
        {
          path: 'topics/:topicId',
          component: () => import('../layouts/TopicLayout.vue'),
          children: [
            {
              path: 'chat',
              name: 'chat',
              components: {
                default: () => import('../views/topics/TopicChatView.vue'),
                title: () => import('@shared/components/chat/TopicTitleView.vue'),
              },
              meta: {
                authRequired: true,
                title: 'Chat with coach',
                hideMainChatBtn: true,
                parentName: 'topics',
                // leftButton: 'back',
              },
            },
            {
              path: 'chat/demo',
              name: 'chat-demo',
              components: {
                default: () => import('../views/topics/TopicChatView.vue'),
                title: () => import('@shared/components/chat/TopicTitleView.vue'),
              },
              meta: {
                authRequired: true,
                title: 'Chat with coach',
                hideMainChatBtn: true,
                parentName: 'topics',
                leftButton: 'back',
                demo: true,
              },
            },
          ],
        },
        {
          path: 'activities/:activityId',
          name: 'activity-detail',
          component: () => import('../layouts/ActivityLayout.vue'),
          children: [
            {
              path: 'survey',
              name: 'activity-survey',
              components: {
                default: () => import('../views/activity/ActivitySurveyView.vue'),
                title: () => import('../views/DefaultTitleView.vue'),
              },
              meta: {
                title: 'Post-workout survey',
                leftButton: 'back',
                authRequired: true,
              },
            },
          ],
        },
        {
          path: 'subscriptions/:subscriptionId/callback',
          name: 'subscriptions-callback',
          component: () => import('../views/SubscriptionPaymentCallback.vue'),
          meta: {
            isSignInPage: false,
            authRequired: true,
          },
        },
        {
          path: 'purchases/:purchaseId/callback',
          name: 'ourchases-callback',
          component: () => import('../views/PurchasePaymentCallback.vue'),
          meta: {
            isSignInPage: false,
            authRequired: true,
          },
        },
        {
          path: '',
          name: 'main',
          component: () => import('../layouts/MainLayout.vue'),
          children: [
            {
              path: '',
              name: 'home',
              redirect: { name: 'dashboard' },
            },
            {
              path: 'dashboard',
              name: 'dashboard',
              components: {
                default: () => import('../views/DashboardView.vue'),
                title: () => import('../views/ActivityFilterTitleView.vue'),
              },
              meta: {
                authRequired: true,
                title: 'Today',
              },
            },
            {
              path: 'calendar',
              name: 'calendar',
              components: {
                default: () => import('../views/CalendarView.vue'),
                title: () => import('../views/CalendarTitleView.vue'),
              },
            },
            {
              path: 'events/upcoming',
              name: 'events-upcoming',
              components: {
                default: () => import('../views/UpcomingEventListView.vue'),
                title: () => import('../views/CalendarTitleView.vue'),
              },
              meta: {
                authRequired: true,
                title: 'Upcoming Events',
              },
            },
            {
              path: 'activities',
              name: 'activities',
              components: {
                default: () => import('../views/HomeView.vue'),
                title: () => import('../views/ActivityFilterTitleView.vue'),
              },
              meta: {
                authRequired: true,
                title: 'Activity',
              },
            },
            {
              path: 'favorites',
              name: 'favorites',
              components: {
                default: () => import('../views/FavoriteMessageView.vue'),
                title: () => import('../views/DefaultTitleView.vue'),
              },
              meta: {
                authRequired: true,
                title: 'Favorites',
                hideMainChatBtn: true,
              },
            },
            {
              path: 'plans',
              name: 'plans',
              components: {
                default: () =>
                  isPayAsYouGoPlanEnabled
                    ? import('../views/PlanListPayPerUseView.vue')
                    : import('../views/PlanListView.vue'),
                title: () => import('../views/DefaultTitleView.vue'),
              },
              meta: {
                authRequired: true,
                title: 'Upgrade plan',
                hideMainChatBtn: true,
                leftButton: 'back',
                parentName: 'home',
                hideFreemiumAlert: true,
              },
            },
            {
              path: 'tasks',
              name: 'tasks',
              components: {
                title: () => import('../views/DefaultTitleView.vue'),
                default: () => import('../views/OnboardingView.vue'),
              },
              meta: {
                authRequired: true,
                title: 'Tasks',
                hideHeader: true,
              },
            },
            {
              path: 'topics',
              name: 'topics',
              components: {
                default: () => import('../views/topics/TopicsView.vue'),
                title: () => import('../components/TopicsTitleView.vue'),
              },
              meta: {
                authRequired: true,
                title: 'Topics',
              },
            },
            {
              path: 'subscriptions',
              name: 'my-subscriptions',
              components: {
                default: () => import('../views/MySubscriptionsView.vue'),
                title: () => import('../views/DefaultTitleView.vue'),
              },
              meta: {
                authRequired: true,
                title: 'My subscriptions',
                hideMainChatBtn: true,
              },
            },
            {
              path: 'stats',
              name: 'user-stats',
              components: {
                default: () => import('../views/UserStatsView.vue'),
                title: () => import('../views/DefaultTitleView.vue'),
              },
              meta: {
                authRequired: true,
                title: 'Running Statistics',
                hideMainChatBtn: true,
                leftButton: 'back',
              },
            },
            {
              path: 'account',
              name: 'account',
              components: {
                default: () => import('../views/AccountView.vue'),
                title: () => import('../views/DefaultTitleView.vue'),
              },
              meta: {
                authRequired: true,
                title: 'My Account',
                hideMainChatBtn: true,
              },
            },
            {
              path: 'profile',
              name: 'profile',
              components: {
                default: () => import('../views/ProfileView.vue'),
                title: () => import('../views/DefaultTitleView.vue'),
              },
              meta: {
                authRequired: true,
                title: 'Profile',
                hideMainChatBtn: true,
                leftButton: 'back',
                parentName: 'activities',
              },
            },
            {
              path: 'target',
              name: 'target',
              components: {
                default: () => import('../views/TargetView.vue'),
                title: () => import('../views/DefaultTitleView.vue'),
              },
              meta: {
                authRequired: true,
                title: 'Target',
                hideMainChatBtn: true,
              },
            },
            {
              path: 'training-plan',
              name: 'training-plan',
              components: {
                default: () => import('../views/TrainingPlanSettingsView.vue'),
                title: () => import('../views/DefaultTitleView.vue'),
              },
              meta: {
                authRequired: true,
                title: 'Training Plan Settings',
                hideMainChatBtn: true,
              },
            },
            {
              path: 'coach',
              name: 'coach',
              components: {
                default: () => import('../views/CoachView.vue'),
                title: () => import('../views/DefaultTitleView.vue'),
              },
              meta: {
                authRequired: true,
                title: 'Coach',
                hideMainChatBtn: true,
              },
            },
            {
              path: 'purchases/:purchaseId/payment',
              name: 'purchase-payment',
              components: {
                default: () => import('../views/PurchasePaymentView.vue'),
                title: () => import('../views/DefaultTitleView.vue'),
              },
              meta: {
                authRequired: true,
                title: 'Subscription',
                hideMainChatBtn: true,
                hideFreemiumAlert: true,
              },
            },
            {
              path: 'purchases/:purchaseId',
              name: 'purchase-detail',
              components: {
                default: () => import('../views/PurchaseView.vue'),
                title: () => import('../views/DefaultTitleView.vue'),
              },
              meta: {
                authRequired: true,
                title: 'Purchase',
                hideMainChatBtn: true,
                hideFreemiumAlert: true,
              },
            },
            {
              path: 'purchases',
              name: 'purchases',
              components: {
                default: () => import('../views/PurchasesView.vue'),
                title: () => import('../views/DefaultTitleView.vue'),
              },
              meta: {
                authRequired: true,
                title: 'Purchase',
                hideMainChatBtn: true,
                hideFreemiumAlert: true,
              },
            },
            {
              path: 'quick-responses',
              name: 'quick-responses',
              components: {
                default: () => import('../views/quick-responses/QuickResponseListView.vue'),
                title: () => import('../views/DefaultTitleView.vue'),
              },
              meta: {
                authRequired: true,
                title: 'Quick responses',
                hideMainChatBtn: true,
              },
            },
            {
              path: 'quick-responses/new',
              name: 'quick-responses-create',
              components: {
                default: () => import('../views/quick-responses/QuickResponseCreateView.vue'),
                title: () => import('../views/DefaultTitleView.vue'),
              },
              meta: {
                authRequired: true,
                title: 'Quick responses',
                hideMainChatBtn: true,
                leftButton: 'back',
                parentName: 'quick-responses',
              },
            },
            {
              path: 'quick-responses/:templateId',
              name: 'quick-responses-detail',
              component: () => import('../layouts/TemplateLayout.vue'),
              meta: {
                authRequired: true,
              },
              children: [
                {
                  path: 'edit',
                  name: 'quick-responses-edit',
                  components: {
                    default: () => import('../views/quick-responses/QuickResponseEditView.vue'),
                    title: () => import('../views/DefaultTitleView.vue'),
                  },
                  meta: {
                    authRequired: true,
                    title: 'Quick responses',
                    hideMainChatBtn: true,
                    leftButton: 'back',
                    parentName: 'quick-responses',
                  },
                },
              ],
            },

            {
              path: 'coaches/:coachId',
              name: 'coaches-detail',
              components: {
                default: () => import('../layouts/CoachLayout.vue'),
                title: () => import('../views/DefaultTitleView.vue'),
              },
              children: [
                {
                  path: 'subscribe',
                  name: 'coaches-subscribe',
                  components: {
                    default: () => import('../views/CoachSubscriptionView.vue'),
                    title: () => import('../views/DefaultTitleView.vue'),
                  },
                  meta: {
                    authRequired: true,
                    title: 'Coach subscription',
                    hideMainChatBtn: true,
                    leftButton: 'back',
                    parentName: 'coaches',
                    upgrade: false,
                    hideAlert: true,
                  },
                },
                {
                  path: 'upgrade',
                  name: 'coaches-upgrade',
                  components: {
                    default: () =>
                      isPayAsYouGoPlanEnabled
                        ? import('../views/CoachPayAsYouGoUpgradeView.vue')
                        : import('../views/CoachSubscriptionView.vue'),
                    title: () => import('../views/DefaultTitleView.vue'),
                  },
                  meta: {
                    authRequired: true,
                    title: 'Upgrade subscription',
                    hideMainChatBtn: true,
                    leftButton: 'back',
                    parentName: 'home',
                    upgrade: true,
                    hideFreemiumAlert: true,
                  },
                },
                {
                  path: 'upgrade/ambassador',
                  name: 'coaches-upgrade-ambassador',
                  components: {
                    default: () => import('../views/CoachSubscriptionAmbassadorView.vue'),
                    title: () => import('../views/DefaultTitleView.vue'),
                  },
                  meta: {
                    authRequired: true,
                    title: 'ambassador.subscription',
                    hideMainChatBtn: true,
                    leftButton: 'back',
                    parentName: 'home',
                    upgrade: true,
                    hideFreemiumAlert: true,
                  },
                },
                // {
                //   path: 'upgrade/pay-as-you-go',
                //   name: 'coaches-upgrade-pay-as-you-go',
                //   components: {
                //     default: () => import('../views/CoachPayAsYouGoUpgradeView.vue'),
                //     title: () => import('../views/DefaultTitleView.vue'),
                //   },
                //   meta: {
                //     authRequired: true,
                //     title: 'Upgrade subscription',
                //     hideMainChatBtn: true,
                //     leftButton: 'back',
                //     parentName: 'home',
                //     upgrade: true,
                //     hideFreemiumAlert: true,
                //     payAsYouGo: true,
                //   },
                // },
                {
                  path: 'subscription/edit',
                  name: 'coaches-subscription-edit',
                  components: {
                    default: () => import('../views/CoachSubscriptionEditView.vue'),
                    title: () => import('../views/DefaultTitleView.vue'),
                  },
                  meta: {
                    authRequired: true,
                    title: 'Update subscription',
                    hideMainChatBtn: true,
                    leftButton: 'back',
                    parentName: 'account',
                    hideFreemiumAlert: true,
                  },
                },
                {
                  path: 'products',
                  name: 'coaches-products',
                  components: {
                    default: () => import('../views/CoachProductView.vue'),
                    title: () => import('../views/DefaultTitleView.vue'),
                  },
                  meta: {
                    authRequired: true,
                    title: 'Coach products',
                    hideMainChatBtn: true,
                    leftButton: 'back',
                    parentName: 'coaches',
                  },
                },
              ],
            },
          ],
        },
      ],
    },
  ],
});

async function checkAndNext(
  options: {
    name: string;
    check: () => Promise<boolean> | boolean;
    next: () => Promise<void> | void;
  }[]
): Promise<boolean> {
  for (const option of options) {
    console.log(`[router] checking ${option.name}`);
    const result = await option.check();
    if (result) {
      console.log(`[router] ${option.name} check passed, calling next()`);
      await option.next();
      return true;
    }
  }
  return false;
}

router.beforeEach(async (to, from, next) => {
  const start = new Date();
  console.log(`[router.auth] start ${start.toISOString()}`);
  const toName = to.name as string;
  const isAnonymousDemoChatRoute = toName === 'chat-demo' && to.meta.demo === true;
  const onboardingPages = ['welcome', 'coaches-subscribe', 'coaches-subscribe-stripe-callback', 'subscription-payment'];

  const simpleRouteChecks = [
    {
      name: 'signin-page',
      check: () => toName === 'signin' || toName === 'signup',
      next: () => {
        console.log('[router] let signin page decide which page to go next');
        next();
      },
    },
    {
      name: 'demo-route',
      check: () => to.name === 'demo',
      next: async () => {
        const { loadAndUpdateCache: loadAndUpdateUserCache } = useUserStore();
        const { userCredential: auth } = await loadAndUpdateUserCache();

        if (auth && !auth.isAnonymous) {
          console.log('[router.auth] non-anonymous user cannot access demo route');
          next({ name: 'dashboard' });
          return;
        }

        next();
      },
    },
    {
      name: 'anonymous-public-route',
      check: () => !to.meta.authRequired && anonymousPublicRouteNames.has(toName),
      next: () => {
        console.log('[router] anonymous public route allowed');
        next();
      },
    },
    {
      name: 'public-route',
      check: () => !to.meta.authRequired,
      next: async () => {
        const { loadAndUpdateCache: loadAndUpdateUserCache } = useUserStore();
        loadAndUpdateUserCache();

        // if (!auth) {
        //   console.log('[router.auth] anonymous user cannot access public route');
        //   next({ name: 'signup' });
        //   return;
        // }

        // identify({ id: user?.id, email: user?.email, stravaId: user?.stravaId, displayName: user?.displayName });

        console.log('[router] auth not required and not signin page');
        next();
      },
    },
  ];

  if (await checkAndNext(simpleRouteChecks)) {
    return;
  }

  const { loadAndUpdateCache: loadAndUpdateUserCache } = useUserStore();
  console.log(`[router.auth] js loaded (${new Date().getTime() - start.getTime()}ms)`);
  const { userCredential: auth, user } = await loadAndUpdateUserCache();
  console.log(`[router.auth] user loaded (${new Date().getTime() - start.getTime()}ms)`);
  const isAnonymousDemoChat = isAnonymousDemoChatRoute && auth?.isAnonymous;

  if (!auth) {
    if (isAnonymousDemoChatRoute) {
      console.log('[router.auth] anonymous demo chat is allowed without onboarding checks');
      next();
      return;
    }

    console.log('[router.auth] user did not sign in go to signin page');
    next({ name: 'demo' });
    return;
  }

  identify({ id: user?.id, email: user?.email, stravaId: user?.stravaId, displayName: user?.displayName });

  console.log(`[router.auth] claims retrieved (${new Date().getTime() - start.getTime()}ms)`);
  console.log('[router.auth] user is signed in', user);

  const authenticatedRouteChecks = [
    {
      name: 'anonymous-user',
      check: () => auth?.isAnonymous && !isAnonymousDemoChatRoute,
      next: () => {
        console.log('[router.auth] anonymous user should be able to access demo route but not other routes');
        next({ name: 'demo' });
      },
    },
    {
      name: 'profile-update-route',
      check: () => toName === 'onboarding-update-profile',
      next: () => {
        console.log('[router] user is going to update profile page');
        next();
      },
    },
    {
      name: 'anonymous-demo-chat',
      check: () => Boolean(isAnonymousDemoChat),
      next: () => {
        console.log('[router.auth] anonymous demo chat is allowed without onboarding checks');
        next();
      },
    },
    {
      name: 'missing-profile',
      check: () => !user?.firstName || !user?.lastName || !user?.email || !user?.phoneNumber,
      next: () => {
        console.log('[router] user need to update profile');
        next({ name: 'onboarding-update-profile' });
      },
    },
    {
      name: 'onboarding-related-page',
      check: () => onboardingPages.includes(toName as string),
      next: () => {
        console.log('[router.auth] user is going to onboarding related page');
        next();
      },
    },
  ];

  if (await checkAndNext(authenticatedRouteChecks)) {
    return;
  }

  // Request user payment or freemium
  const { loadAndUpdateCache: loadAndUpdateCoachCache } = useCoachStore();
  const links = await loadAndUpdateCoachCache();
  console.log(`[router.auth] coaches retrieved (${new Date().getTime() - start.getTime()}ms) ${links.length} links`);
  if (links.length === 0) {
    console.log(`[router.auth] user has no coach go to coaches page (${new Date().getTime() - start.getTime()}ms)`);
    next({ name: 'welcome' });
    return;
  }

  console.log(
    `[router.auth] user already signed in continue with current route (${new Date().getTime() - start.getTime()}ms)`
  );
  next();
});

router.afterEach((to, from) => {
  // Page level analysis should be afterEach to ensure user data is
  // loaded and identify is called before tagging page view
  config('app', { name: 'runner' });
  config('page_path', { path: to.fullPath });
  tag('Page View', {
    page: to.fullPath,
    referrer: from.fullPath,
    documentReferer: document.referrer,
    ua: navigator.userAgent,
    fromChannel: to.query.channel || to.meta.channel || 'direct',
    refCode: to.params.refCode || to.query.ref || null,
    notificationId: to.query.notificationId || null,
  });
});

// Hide loading indicator after initial navigation completes
router.afterEach(() => {
  if (loadingState.isInitialLoad) {
    loadingState.isInitialLoad = false;
    // Add a small delay to ensure view is fully rendered
    setTimeout(() => {
      hideLoadingIndicator();
    }, 300);
  }
});

router.onError((error, to) => {
  console.warn('[router.error]', error);
  if (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed') ||
    error.message.includes('Unable to preload CSS for')
  ) {
    if (!to?.fullPath) {
      window.location.reload();
    } else {
      window.location = to.fullPath as string & Location;
    }
  }
});

export default router;
